import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash/debounce';
import { searchProductsStartsWith } from 'services/stock-location/productService';

// Dropdown search backed by GET sls-products/search-starts-with (SlsProductController).
// `field` picks which SearchStartsWithSlsProductRQ param the typed text is sent as
// (productCode | supplierCode | description); the option carries the full SlsProductRS
// row so callers can auto-populate the rest of a form from whichever field was searched.
const FIELD_TO_LABEL = {
  productCode: product => product.productCode,
  supplierCode: product => product.supplierCode,
  description: product => product.productDescription
};

const ProductStartsWithDropdown = ({
  field,
  value,
  onSelect,
  isDisabled,
  placeholder
}) => {
  const getLabel = FIELD_TO_LABEL[field];

  const loadOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        if (!inputValue) {
          callback([]);
          return;
        }
        searchProductsStartsWith({ [field]: inputValue })
          .then(res => {
            const products = res?.data || [];
            const seenLabels = new Set();
            const options = [];
            products.forEach(product => {
              const label = getLabel(product);
              const dedupeKey = `${label ?? ''}`.trim().toLowerCase();
              if (seenLabels.has(dedupeKey)) return;
              seenLabels.add(dedupeKey);
              options.push({
                value: product.productId,
                label,
                product
              });
            });
            callback(options);
          })
          .catch(() => callback([]));
      }, 500),
    [field, getLabel]
  );

  const handleChange = useCallback(
    option => onSelect(option ? option.product : null),
    [onSelect]
  );

  return (
    <AsyncSelect
      isDisabled={isDisabled}
      isClearable
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      value={value ? { value, label: value } : null}
      onChange={handleChange}
      placeholder={placeholder}
      classNamePrefix="react-select"
      menuPortalTarget={document.body}
      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
    />
  );
};

ProductStartsWithDropdown.propTypes = {
  field: PropTypes.oneOf(['productCode', 'supplierCode', 'description'])
    .isRequired,
  value: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string
};

export default ProductStartsWithDropdown;
